import {DISPUTE_TYPE} from '../data/reference'
import {deadline} from '../format'
import {governingType} from './query'

/**
 * Grouping and aggregation for the "By product" triage view.
 *
 * The premise, from the mockup's proto-triage.js: a merchant with 1,000
 * discrepancies does not have 1,000 problems. They have ~25 SKUs and 7
 * couriers. The same product, packed the same way, is re-measured the same
 * way every time — so the queue is not rows, it is cause-groups, each
 * carrying one aggregate verdict.
 *
 * Kept pure and React-free, like state/query.js and state/analytics.js, so
 * the arithmetic is testable without mounting anything.
 */

export const GROUP_KEYS = [
  {
    id: 'sku',
    label: 'Product SKU',
    note: 'Same product, same carton, same re-measurement.',
  },
  {
    id: 'skutype',
    label: 'SKU × Type',
    note: 'Strict cause key — a SKU billed two different ways splits in two.',
  },
  {
    id: 'courier',
    label: 'Courier',
    note: 'One courier, one hub, one scanner calibration.',
  },
]

/**
 * The triage view has no sort control of its own — FilterBar's already sits
 * directly above it, and two sort controls on one screen disagreeing about the
 * order is worse than either. This maps that control's value onto a group
 * ordering so the label the merchant can see is the order they actually get.
 */
export const SORT_FROM_FILTER = {
  urgency: 'deadline',
  excess: 'money',
  newest: 'newest',
}

/**
 * Open means still awaiting the merchant's decision. Only NEW rows carry a
 * running auto-accept clock and only they can be accepted or disputed in
 * bulk, so every "at stake" figure on this view is scoped to them.
 */
export function isOpen(row) {
  return row.status === 'NEW'
}

/**
 * The grouping key. `row.sku || row.product` mirrors analytics.js's
 * skuBreakdown deliberately: four rows in the dataset carry an empty sku,
 * and keying on sku alone would collapse all four into one meaningless
 * group. Type comes from governingType(), never row.type — the stored field
 * was never reconciled against the dead/vol split and can disagree with it
 * (see query.js).
 */
function keyOf(row, groupBy) {
  const sku = row.sku || row.product
  if (groupBy === 'courier') return row.courier
  if (groupBy === 'skutype') return sku + ' • ' + governingType(row)
  return sku
}

function shortType(type) {
  return DISPUTE_TYPE[type] ? DISPUTE_TYPE[type].short : type
}

function uniq(list) {
  return Array.from(new Set(list))
}

/**
 * Everything a card needs about one group, computed in a single pass.
 *
 * `stake` is the signed sum over OPEN members only — what is still winnable
 * or losable. `totalExcess` spans every member including settled ones, and
 * is what a fully-decided group shows instead. `soon` is the nearest
 * auto-accept deadline among open members, which drives both the clock
 * column and the card's urgency rail.
 */
export function groupStat(members) {
  const open = members.filter(isOpen)
  let soon = null

  open.forEach((row) => {
    const c = deadline(row.deadlineAt)
    if (!soon || c.hours < soon.hours) soon = c
  })

  const hasEvidence = (row) => Boolean(row.hasVideo) || (row.merchantImages || 0) > 0

  return {
    n: members.length,
    openN: open.length,
    stake: open.reduce((a, r) => a + (r.excessAmount || 0), 0),
    totalExcess: members.reduce((a, r) => a + (r.excessAmount || 0), 0),
    excessKg: open.reduce((a, r) => a + (r.excessKg || 0), 0),
    soon,
    evOpen: open.filter(hasEvidence).length,
    settled: members.length - open.length,
    // The group's most recent discrepancy, for the "Date raised" ordering.
    newest: members.reduce((a, r) => Math.max(a, new Date(r.createdAt).getTime() || 0), 0),
  }
}

/**
 * The urgency rail down the left of each card: red inside 24h, amber inside
 * 72h, green beyond, grey when nothing is open. Reuses deadline()'s own
 * level thresholds rather than inventing a second set.
 */
export function groupLevel(stat) {
  if (!stat.openN) return 'none'
  return stat.soon ? stat.soon.level : 'ok'
}

/**
 * The meta line under the title — "2 shipments · 1 Higher Dead · 1 Dead +
 * Vol · Shadowfax". Types are ordered by descending frequency, and collapse
 * to "all X" when the group only misbehaves one way.
 */
export function patternText(members, groupBy) {
  const n = members.length
  const parts = [n + (n === 1 ? ' shipment' : ' shipments')]

  const counts = {}
  members.forEach((r) => {
    const t = governingType(r)
    counts[t] = (counts[t] || 0) + 1
  })
  const types = Object.keys(counts).sort((a, b) => counts[b] - counts[a])
  if (types.length === 1) {
    parts.push('all ' + shortType(types[0]))
  } else {
    types.forEach((t) => parts.push(counts[t] + ' ' + shortType(t)))
  }

  if (groupBy === 'courier') {
    // The mockup shows a category count here. This dataset has no category
    // field and inventing one would be fabricated data in a module whose
    // dataset is annotated real-vs-synthesized line by line, so the honest
    // equivalent is how many distinct products the courier is mis-measuring.
    const skus = uniq(members.map((r) => r.sku || r.product))
    parts.push(skus.length + (skus.length === 1 ? ' product' : ' products'))
  } else {
    const couriers = uniq(members.map((r) => r.courier))
    if (couriers.length <= 2) parts.push(couriers.join(' + '))
    else parts.push(couriers.slice(0, 2).join(', ') + ' +' + (couriers.length - 2))
  }

  return parts.join(' · ')
}

function rangeOf(values) {
  const list = values.length ? values : [0]
  const min = Math.min(...list)
  const max = Math.max(...list)
  // 0.005 kg — below the 2dp the whole module formats weights at, so a value
  // that only differs in invisible digits is not reported as a range.
  return {min, max, varies: max - min > 0.005}
}

/**
 * The weights the group is arguing about: what the merchant declared, what the
 * courier billed, and the gap. Scoped to OPEN members when there are any —
 * settled rows are history and would widen the range with weights nobody can
 * act on any more.
 *
 * Each side can be a range rather than a figure. Under Product SKU grouping
 * `declared` is normally constant (it is the SKU's own declared weight) while
 * `billed` varies with how each courier measured it, which is exactly the
 * disagreement the group exists to show. Under Courier grouping both vary.
 */
export function weightSummary(members) {
  const open = (members || []).filter(isOpen)
  const src = open.length ? open : (members || [])
  // Which side governs, across the whole group. Bolding one side is a claim,
  // so it is only made when every member agrees; a group billed volumetrically
  // on one shipment and by dead weight on another governs neither, and says so.
  const types = Array.from(new Set(src.map((r) => governingType(r))))
  const governs = types.length === 1 ? types[0] : 'MIXED'

  return {
    declared: rangeOf(src.map((r) => r.originalWeight || 0)),
    billed: rangeOf(src.map((r) => r.appliedWeight || 0)),
    excess: rangeOf(src.map((r) => r.excessKg || 0)),
    dead: rangeOf(src.map((r) => r.deadKg || 0)),
    vol: rangeOf(src.map((r) => r.volKg || 0)),
    declaredVol: rangeOf(src.map((r) => r.declaredVolKg || 0)),
    governs,
  }
}

/**
 * Buckets rows in first-appearance order. Every display string comes from
 * the group's first member, which is safe because the key already pins the
 * product (or courier) the card names.
 */
export function groupRows(rows, groupBy) {
  const byKey = new Map()

  ;(rows || []).forEach((row) => {
    const key = keyOf(row, groupBy)
    if (!byKey.has(key)) byKey.set(key, {key, first: row, members: []})
    byKey.get(key).members.push(row)
  })

  return Array.from(byKey.values()).map((g) => ({
    key: g.key,
    title: groupBy === 'courier' ? g.first.courier : g.first.product,
    code: groupBy === 'courier' ? null : g.first.sku || null,
    typeShort: groupBy === 'skutype' ? shortType(governingType(g.first)) : null,
    members: g.members,
    pattern: patternText(g.members, groupBy),
    weights: weightSummary(g.members),
    stat: groupStat(g.members),
  }))
}

/**
 * money: most at stake first, so the biggest recoverable sum is the first
 * decision. deadline: nearest auto-accept first — groups with no clock sink
 * to the bottom rather than sorting as zero, which would put settled work
 * above live work.
 */
export function orderGroups(groups, sort) {
  const list = (groups || []).slice()
  if (sort === 'newest') {
    list.sort((a, b) => {
      if (b.stat.newest !== a.stat.newest) return b.stat.newest - a.stat.newest
      return b.stat.stake - a.stat.stake
    })
    return list
  }
  if (sort === 'deadline') {
    list.sort((a, b) => {
      const am = a.stat.soon ? a.stat.soon.hours : Infinity
      const bm = b.stat.soon ? b.stat.soon.hours : Infinity
      if (am !== bm) return am - bm
      return b.stat.stake - a.stat.stake
    })
    return list
  }
  list.sort((a, b) => {
    if (b.stat.stake !== a.stat.stake) return b.stat.stake - a.stat.stake
    if (b.stat.totalExcess !== a.stat.totalExcess) return b.stat.totalExcess - a.stat.totalExcess
    return b.stat.n - a.stat.n
  })
  return list
}

/**
 * The header card's arithmetic. `ratio` is the whole point of the view —
 * rows per decision — and is reported against a 1,000-row backlog because
 * that is the scale the grouping argument is actually about.
 *
 * `decided` counts groups the merchant has acted on in this session, which
 * the caller tracks; it is passed in rather than derived, since a verdict
 * lives in component state, not in the dataset.
 */
export function triageTotals(groups, decidedKeys) {
  const list = groups || []
  const decided = decidedKeys || new Set()

  let stake = 0
  let openN = 0
  let needs = 0
  let soon = null
  let soonGroup = null

  list.forEach((g) => {
    stake += g.stat.stake
    openN += g.stat.openN
    if (g.stat.openN) needs += 1
    if (g.stat.soon && (!soon || g.stat.soon.hours < soon.hours)) {
      soon = g.stat.soon
      soonGroup = g
    }
  })

  const rowCount = list.reduce((a, g) => a + g.stat.n, 0)
  const ratio = list.length ? rowCount / list.length : 0

  return {
    rows: rowCount,
    groups: list.length,
    ratio,
    // Only meaningful once grouping actually reduces anything; below 1.05 the
    // card says so instead of printing a flattering "1.0×".
    reduces: ratio >= 1.05,
    projected: ratio ? Math.round(1000 / ratio) : 0,
    singles: list.filter((g) => g.stat.n === 1).length,
    stake,
    openN,
    needs,
    soon,
    soonLabel: soon ? soon.label.replace(/ left$/, '') : null,
    soonTitle: soonGroup ? soonGroup.title : null,
    decided: list.filter((g) => decided.has(g.key)).length,
  }
}
